import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Camera, Download, FileText, Pencil, Plus, Trash2, Upload, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import api, { fileToBase64, fmtErr, openPdf } from "../api";
import ExpenseDialog from "../components/ExpenseDialog";
import FuelDialog from "../components/FuelDialog";
import StatusBadge from "../components/StatusBadge";
import TireDialog from "../components/TireDialog";
import { useAuth } from "../context/AuthContext";
import VehicleFormDialog from "../components/VehicleFormDialog";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtDate, fmtDateTime, fmtMoney, fmtNum, todayISO } from "../utils/format";

const fmt = fmtMoney;
const InfoRow = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
    <p className="text-sm font-medium text-slate-800">{value || "—"}</p>
  </div>
);


function MiniTable({ columns, rows, empty = "Aucune donnée" }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-slate-50 text-left">
            {columns.map((c) => <th key={c.label} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-slate-400">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-50 last:border-0">
              {columns.map((c) => <td key={c.label} className="px-3 py-2">{c.render(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const expCols = (onDelete) => [
  { label: "Date", render: (e) => fmtDate(e.date) },
  { label: "Catégorie", render: (e) => <span className="font-medium">{e.category}</span> },
  { label: "Description", render: (e) => e.description || "—" },
  { label: "Montant", render: (e) => (
    <div>
      <p className="font-mono-num font-semibold">{fmt(e.amount_fcfa)}</p>
      {e.currency !== "FCFA" && <p className="text-xs text-slate-400">{fmtNum(e.amount)} {e.currency}</p>}
    </div>
  ) },
  { label: "Paiement", render: (e) => e.payment_method },
  { label: "Par", render: (e) => <span className="text-xs text-slate-500">{e.created_by}</span> },
  ...(onDelete ? [{ label: "", render: (e) => (
    <button onClick={() => onDelete(e)} className="text-slate-300 hover:text-red-500" data-testid={`delete-expense-${e.id}`}><Trash2 className="h-4 w-4" /></button>
  ) }] : []),
];

function MaintenanceDialog({ vehicleId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ date: todayISO(), mileage: "", type: "Vidange", garage: "", parts: "", labor_cost: "", parts_cost: "", next_date: "", next_mileage: "", invoice_ref: "", account_id: "", notes: "" });
  useEffect(() => {
    if (!open) return;
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
  }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try {
      await api.post("/maintenance", { ...form, vehicle_id: vehicleId, labor_cost: Number(form.labor_cost) || 0, parts_cost: Number(form.parts_cost) || 0, account_id: form.account_id || null, next_date: form.next_date || null, next_mileage: form.next_mileage ? Number(form.next_mileage) : null, mileage: form.mileage ? Number(form.mileage) : null });
      toast.success("Entretien enregistré — ajouté au coût de revient");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="add-maintenance-button"><Plus className="mr-1 h-4 w-4" /> Nouvel entretien</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nouvel entretien</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="maint-date" /></div>
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="maint-type"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.maintenance_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Kilométrage</Label><Input type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} data-testid="maint-mileage" /></div>
          <div><Label>Garage</Label><Input value={form.garage} onChange={(e) => set("garage", e.target.value)} data-testid="maint-garage" /></div>
          <div><Label>Main-d'œuvre (FCFA)</Label><Input type="number" value={form.labor_cost} onChange={(e) => set("labor_cost", e.target.value)} data-testid="maint-labor" /></div>
          <div><Label>Pièces (FCFA)</Label><Input type="number" value={form.parts_cost} onChange={(e) => set("parts_cost", e.target.value)} data-testid="maint-parts-cost" /></div>
          <div className="col-span-2"><Label>Pièces utilisées</Label><Input value={form.parts} onChange={(e) => set("parts", e.target.value)} /></div>
          <div><Label>Prochain entretien</Label><Input type="date" value={form.next_date} onChange={(e) => set("next_date", e.target.value)} data-testid="maint-next-date" /></div>
          <div><Label>Prochain kilométrage</Label><Input type="number" value={form.next_mileage} onChange={(e) => set("next_mileage", e.target.value)} /></div>
          <div><Label>N° facture</Label><Input value={form.invoice_ref} onChange={(e) => set("invoice_ref", e.target.value)} /></div>
          <div><Label>Compte débité</Label>
            <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="maint-submit">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  ["infos", "Infos"], ["photos", "Photos"], ["achat", "Achat"], ["importation", "Importation"],
  ["port", "Port"], ["douane", "Douane"], ["depenses", "Dépenses"], ["entretien", "Entretien"],
  ["carburant", "Carburant"], ["pneus", "Pneus"],
  ["locations", "Locations"], ["vente", "Vente"], ["paiements", "Paiements"], ["documents", "Documents"],
  ["historique", "Historique"], ["rentabilite", "Rentabilité"],
];

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [tires, setTires] = useState([]);
  const photoInput = useRef(null);
  const [docForm, setDocForm] = useState({ name: "", doc_type: "Autre", data: null });
  const [docOpen, setDocOpen] = useState(false);

  const load = () => {
    api.get(`/vehicles/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Véhicule introuvable"));
    api.get(`/fuel?vehicle_id=${id}`).then((r) => setFuelLogs(r.data)).catch(() => {});
    api.get(`/tires?vehicle_id=${id}`).then((r) => setTires(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="flex h-96 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#2563EB] border-t-transparent" /></div>;

  const { vehicle: v, expenses, maintenances, rentals, sales, credits, documents, purchases, timeline } = data;
  const label = `${v.brand} ${v.model} (${v.internal_number})`;

  const delExpense = async (e) => {
    if (!window.confirm(`Supprimer la dépense « ${e.category} » ?`)) return;
    await api.delete(`/expenses/${e.id}`);
    toast.success("Dépense supprimée");
    load();
  };

  const expGroup = (g) => expenses.filter((e) => e.group === g);
  const setStatus = async (status) => {
    await api.patch(`/vehicles/${v.id}`, { status });
    toast.success(`Statut mis à jour : ${status}`);
    load();
  };

  const uploadPhotos = async (files) => {
    for (const f of files) {
      const data64 = await fileToBase64(f);
      await api.post(`/vehicles/${v.id}/photos`, { data: data64 });
    }
    toast.success("Photo(s) ajoutée(s)");
    load();
  };

  const banner = [
    { label: "PRIX D'ACHAT", value: fmt(v.purchase_price_fcfa), sub: v.purchase_currency !== "FCFA" ? `${fmtNum(v.purchase_price)} ${v.purchase_currency}` : null, testid: "vehicle-purchase-price", cls: "text-slate-900" },
    { label: "TOTAL DÉPENSES", value: fmt(v.total_expenses), sub: `${expenses.length} dépense(s)`, testid: "vehicle-total-expenses", cls: "text-amber-600" },
    { label: "COÛT DE REVIENT", value: fmt(v.cost_basis), sub: "Achat + toutes dépenses", testid: "vehicle-cost-basis", cls: "text-blue-700", highlight: true },
    { label: "PRIX DE VENTE", value: v.sale_price != null ? fmt(v.sale_price) : v.target_sale_price ? `${fmt(v.target_sale_price)} (cible)` : "—", sub: v.sale_price != null ? "Réalisé" : "Cible", testid: "vehicle-sale-price", cls: "text-slate-900" },
    { label: "BÉNÉFICE NET", value: v.profit != null ? fmt(v.profit) : "—", sub: v.margin_pct != null ? `Marge ${v.margin_pct}%` : (v.rental_revenue ? `Locations : ${fmt(v.rental_revenue)}` : null), testid: "vehicle-profit", cls: v.profit >= 0 ? "text-emerald-600" : "text-red-600" },
    { label: "MARGE %", value: v.margin_pct != null ? `${v.margin_pct} %` : "—", sub: v.margin_pct != null ? "Bénéfice / coût de revient" : "Non vendu", testid: "vehicle-margin", cls: "text-cyan-700" },
  ];

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/vehicules")} data-testid="back-to-vehicles"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{v.brand} {v.model} {v.year}</h1>
            <StatusBadge status={v.status} />
          </div>
          <p className="mt-0.5 text-sm text-slate-500 font-mono-num">{v.internal_number} · VIN {v.vin || "—"} · {v.plate || "non immatriculé"}</p>
        </div>
        <VehicleFormDialog vehicle={v} onSaved={load} trigger={<Button variant="outline" data-testid="edit-vehicle-button"><Pencil className="mr-1 h-4 w-4" /> Modifier</Button>} />
        {user?.role === "admin" && (
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-vehicle-button"
            onClick={async () => {
              if (!window.confirm(`Supprimer définitivement ${label} et tout son historique ?`)) return;
              try {
                await api.delete(`/vehicles/${v.id}`);
                toast.success("Véhicule supprimé");
                navigate("/vehicules");
              } catch (e) { toast.error(fmtErr(e)); }
            }}>
            <Trash2 className="mr-1 h-4 w-4" /> Supprimer
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="financial-banner">
        {banner.map((b) => (
          <div key={b.label} className={`rounded-xl border p-4 shadow-sm ${b.highlight ? "border-blue-300 bg-blue-50/60" : "border-slate-200 bg-white"}`} data-testid={b.testid}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{b.label}</p>
            <p className={`font-mono-num mt-1.5 text-lg font-bold leading-tight ${b.cls}`}>{b.value}</p>
            {b.sub && <p className="mt-0.5 text-xs text-slate-400">{b.sub}</p>}
          </div>
        ))}
      </div>

      <Tabs defaultValue="infos">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-white border border-slate-200 p-1">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} className="text-xs" data-testid={`tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="infos" className="mt-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <InfoRow label="Catégorie" value={v.category} />
            <InfoRow label="Carburant" value={v.fuel_type} />
            <InfoRow label="Boîte" value={v.transmission} />
            <InfoRow label="Couleur" value={v.color} />
            <InfoRow label="Kilométrage" value={v.mileage ? `${fmtNum(v.mileage)} km` : null} />
            <InfoRow label="N° moteur" value={v.engine_number} />
            <InfoRow label="Date d'achat" value={fmtDate(v.purchase_date)} />
            <InfoRow label="Fournisseur" value={v.supplier_name} />
            <InfoRow label="Pays d'origine" value={v.origin_country} />
            <InfoRow label="Port de départ" value={v.departure_port} />
            <InfoRow label="Port d'arrivée" value={v.arrival_port} />
            <InfoRow label="Arrivée au port" value={fmtDate(v.port_arrival_date)} />
            <InfoRow label="Dédouanement" value={fmtDate(v.customs_date)} />
            <InfoRow label="Revenus location" value={fmt(v.rental_revenue)} />
            <InfoRow label="Créé par" value={v.created_by} />
          </div>
          {v.notes && <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{v.notes}</p>}
        </TabsContent>

        <TabsContent value="photos" className="mt-4 space-y-4">
          <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files && uploadPhotos([...e.target.files])} />
          <Button variant="outline" onClick={() => photoInput.current?.click()} data-testid="upload-photos-button"><Camera className="mr-1 h-4 w-4" /> Ajouter des photos</Button>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {(v.photos || []).length === 0 && <p className="col-span-full py-8 text-center text-sm text-slate-400">Aucune photo. Ajoutez la photo principale et des photos du véhicule.</p>}
            {(v.photos || []).map((p, i) => (
              <div key={i} className="group relative overflow-hidden rounded-lg border border-slate-200">
                <img src={p} alt="" className="h-40 w-full object-cover" />
                {i === 0 && <span className="absolute left-2 top-2 rounded bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white">PRINCIPALE</span>}
                <button onClick={async () => { await api.delete(`/vehicles/${v.id}/photos/${i}`); load(); }}
                  className="absolute right-2 top-2 rounded bg-white/90 p-1 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="achat" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Achat & fournisseur</h3>
            <ExpenseDialog vehicleId={v.id} vehicleLabel={label} defaultGroup="Achat" onSaved={load} triggerLabel="Dépense d'achat" />
          </div>
          <MiniTable columns={[
            { label: "N° Achat", render: (p) => <span className="font-mono-num font-semibold">{p.purchase_number}</span> },
            { label: "Date", render: (p) => fmtDate(p.purchase_date) },
            { label: "Fournisseur", render: (p) => p.supplier_name },
            { label: "Prix", render: (p) => <span className="font-mono-num">{fmt(p.price_fcfa)}{p.currency !== "FCFA" && <span className="block text-xs text-slate-400">{fmtNum(p.price)} {p.currency}</span>}</span> },
            { label: "Payé", render: (p) => fmt(p.paid_amount) },
            { label: "Reste", render: (p) => <span className={p.remaining > 0 ? "font-semibold text-red-600" : "text-emerald-600"}>{fmt(p.remaining)}</span> },
            { label: "Échéance", render: (p) => fmtDate(p.due_date) },
          ]} rows={purchases} empty="Aucun achat enregistré" />
          <MiniTable columns={expCols(delExpense)} rows={expGroup("Achat")} empty="Aucune dépense d'achat (commission, transfert…)" />
        </TabsContent>

        <TabsContent value="importation" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-heading font-semibold text-slate-800">Transport international</h3>
            <div className="flex flex-wrap gap-2">
              {["En transit", "Arrivé au port", "En dédouanement", "Dédouané", "Disponible"].map((s) => (
                <Button key={s} size="sm" variant="outline" disabled={v.status === s} onClick={() => setStatus(s)} data-testid={`set-status-${s.toLowerCase().replace(/[^a-zà-ÿ]/g, "-")}`}>{s}</Button>
              ))}
              <ExpenseDialog vehicleId={v.id} vehicleLabel={label} defaultGroup="Transport international" onSaved={load} triggerLabel="Frais de transport" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <InfoRow label="Pays d'origine" value={v.origin_country} />
            <InfoRow label="Port de départ" value={v.departure_port} />
            <InfoRow label="Port d'arrivée" value={v.arrival_port} />
            <InfoRow label="Statut actuel" value={v.status} />
          </div>
          <MiniTable columns={expCols(delExpense)} rows={expGroup("Transport international")} empty="Aucun frais de transport international" />
        </TabsContent>

        <TabsContent value="port" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Frais de port</h3>
            <ExpenseDialog vehicleId={v.id} vehicleLabel={label} defaultGroup="Port" onSaved={load} triggerLabel="Frais portuaires" />
          </div>
          <InfoRow label="Date d'arrivée au port" value={fmtDate(v.port_arrival_date)} />
          <MiniTable columns={expCols(delExpense)} rows={expGroup("Port")} empty="Aucun frais de port" />
        </TabsContent>

        <TabsContent value="douane" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Dédouanement</h3>
            <ExpenseDialog vehicleId={v.id} vehicleLabel={label} defaultGroup="Dédouanement" onSaved={load} triggerLabel="Frais de douane" />
          </div>
          <InfoRow label="Date de dédouanement" value={fmtDate(v.customs_date)} />
          <MiniTable columns={expCols(delExpense)} rows={expGroup("Dédouanement")} empty="Aucun frais de douane" />
        </TabsContent>

        <TabsContent value="depenses" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Toutes les dépenses — {fmt(v.total_expenses)}</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openPdf(`/vehicles/${v.id}/expenses-report.pdf`)} data-testid="expenses-report-pdf">
                <FileText className="mr-1 h-4 w-4" /> Rapport PDF
              </Button>
              <ExpenseDialog vehicleId={v.id} vehicleLabel={label} onSaved={load} />
            </div>
          </div>
          <MiniTable columns={[{ label: "N°", render: (e) => <span className="font-mono-num text-xs font-semibold">{e.expense_number || "—"}</span> }, { label: "Groupe", render: (e) => <span className="text-xs font-semibold text-slate-500">{e.group}</span> }, ...expCols(delExpense), { label: "Statut", render: (e) => <StatusBadge status={e.status || "Approuvée"} /> }]} rows={expenses} empty="Aucune dépense enregistrée" />
        </TabsContent>

        <TabsContent value="entretien" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Entretiens & réparations</h3>
            <MaintenanceDialog vehicleId={v.id} onSaved={load} />
          </div>
          <MiniTable columns={[
            { label: "Date", render: (m) => fmtDate(m.date) },
            { label: "Type", render: (m) => <span className="font-medium">{m.type}</span> },
            { label: "Garage", render: (m) => m.garage || "—" },
            { label: "Pièces", render: (m) => m.parts || "—" },
            { label: "Coût", render: (m) => <span className="font-mono-num font-semibold">{fmt(m.cost)}</span> },
            { label: "Prochain", render: (m) => m.next_date ? `${fmtDate(m.next_date)}${m.next_mileage ? ` / ${fmtNum(m.next_mileage)} km` : ""}` : "—" },
          ]} rows={maintenances} empty="Aucun entretien enregistré" />
        </TabsContent>

        <TabsContent value="carburant" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Carburant — {fmt(fuelLogs.reduce((s, f) => s + (f.total || 0), 0))}</h3>
            <FuelDialog vehicleId={v.id} onSaved={load} />
          </div>
          <MiniTable columns={[
            { label: "Date", render: (f) => fmtDate(f.date) },
            { label: "Km", render: (f) => fmtNum(f.mileage) },
            { label: "Litres", render: (f) => fmtNum(f.liters) },
            { label: "Prix/L", render: (f) => fmtNum(f.price_per_liter) },
            { label: "Total", render: (f) => <span className="font-mono-num font-semibold">{fmt(f.total)}</span> },
            { label: "Station", render: (f) => f.station || "—" },
            { label: "Conducteur", render: (f) => f.driver || "—" },
          ]} rows={fuelLogs} empty="Aucun ravitaillement enregistré" />
        </TabsContent>

        <TabsContent value="pneus" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Pneus — {fmt(tires.reduce((s, t) => s + (t.price || 0), 0))}</h3>
            <TireDialog vehicleId={v.id} onSaved={load} />
          </div>
          <MiniTable columns={[
            { label: "Marque", render: (t) => <span className="font-medium">{t.brand}</span> },
            { label: "Dimension", render: (t) => t.dimension || "—" },
            { label: "Qté", render: (t) => t.quantity },
            { label: "Prix", render: (t) => <span className="font-mono-num">{fmt(t.price)}</span> },
            { label: "Montage", render: (t) => t.mount_date ? `${fmtDate(t.mount_date)} / ${fmtNum(t.mount_mileage)} km` : "—" },
            { label: "Position", render: (t) => t.position || "—" },
            { label: "Coût/km", render: (t) => t.cost_per_km != null ? `${fmtNum(t.cost_per_km)} FCFA` : (t.removed_date ? "—" : "En service") },
          ]} rows={tires} empty="Aucun pneu enregistré" />
        </TabsContent>

        <TabsContent value="locations" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Locations — revenus : {fmt(v.rental_revenue)}</h3>
            <Button size="sm" variant="outline" onClick={() => navigate("/locations")} data-testid="goto-rentals">Gérer les locations</Button>
          </div>
          <MiniTable columns={[
            { label: "N°", render: (r) => <span className="font-mono-num font-semibold">{r.rental_number}</span> },
            { label: "Client", render: (r) => r.customer_name },
            { label: "Départ", render: (r) => fmtDateTime(r.start_datetime) },
            { label: "Retour prévu", render: (r) => fmtDateTime(r.expected_return) },
            { label: "Statut", render: (r) => <StatusBadge status={r.status} /> },
            { label: "Montant", render: (r) => fmt(r.total_amount) },
          ]} rows={rentals} empty="Aucune location" />
        </TabsContent>

        <TabsContent value="vente" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">Vente</h3>
            {v.status !== "Vendu" && <Button size="sm" onClick={() => navigate("/ventes")} data-testid="goto-sales">Enregistrer une vente</Button>}
          </div>
          <MiniTable columns={[
            { label: "N° Vente", render: (s) => <span className="font-mono-num font-semibold">{s.sale_number}</span> },
            { label: "Date", render: (s) => fmtDate(s.sale_date) },
            { label: "Client", render: (s) => s.customer_name },
            { label: "Prix final", render: (s) => <span className="font-mono-num font-semibold">{fmt(s.final_price)}</span> },
            { label: "Payé", render: (s) => fmt(s.paid_amount) },
            { label: "Reste", render: (s) => <span className={s.remaining > 0 ? "text-red-600 font-semibold" : "text-emerald-600"}>{fmt(s.remaining)}</span> },
            { label: "Statut", render: (s) => <StatusBadge status={s.status} /> },
            { label: "", render: (s) => <Button size="sm" variant="ghost" onClick={() => openPdf(`/sales/${s.id}/invoice.pdf`)} data-testid={`invoice-${s.sale_number}`}><FileText className="h-4 w-4" /></Button> },
          ]} rows={sales} empty="Véhicule non vendu" />
        </TabsContent>

        <TabsContent value="paiements" className="mt-4 space-y-4">
          <h3 className="font-heading font-semibold text-slate-800">Échéanciers de crédit</h3>
          {credits.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Aucun crédit lié à ce véhicule.</p>}
          {credits.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{c.customer_name} — {c.sale_number}</p>
                <StatusBadge status={c.status} />
              </div>
              <p className="text-sm text-slate-500">Total {fmt(c.total)} · Payé {fmt(c.paid)} · Reste <span className="font-semibold text-red-600">{fmt(c.remaining)}</span></p>
              <MiniTable columns={[
                { label: "N°", render: (i) => i.n },
                { label: "Échéance", render: (i) => fmtDate(i.due_date) },
                { label: "Montant", render: (i) => fmt(i.amount) },
                { label: "Payé", render: (i) => fmt(i.paid_amount) },
                { label: "Statut", render: (i) => <StatusBadge status={i.status} /> },
              ]} rows={c.installments} />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="documents" className="mt-4 space-y-4">
          <Dialog open={docOpen} onOpenChange={setDocOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-document-button"><Upload className="mr-1 h-4 w-4" /> Joindre un document</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nom</Label><Input value={docForm.name} onChange={(e) => setDocForm({ ...docForm, name: e.target.value })} data-testid="doc-name-input" /></div>
                <div><Label>Type</Label>
                  <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Facture d'achat", "Certificat d'origine", "Connaissement", "Documents du port", "Documents douaniers", "Facture de transport", "Facture d'entretien", "Assurance", "Carte grise", "Certificat de visite technique", "Contrat de vente", "Contrat de location", "Autre"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Fichier</Label><Input type="file" data-testid="doc-file-input" onChange={async (e) => setDocForm({ ...docForm, data: e.target.files?.[0] ? await fileToBase64(e.target.files[0]) : null })} /></div>
              </div>
              <DialogFooter>
                <Button data-testid="doc-submit-button" onClick={async () => {
                  if (!docForm.data) return toast.error("Fichier requis");
                  await api.post(`/vehicles/${v.id}/documents`, docForm);
                  toast.success("Document ajouté");
                  setDocOpen(false);
                  setDocForm({ name: "", doc_type: "Autre", data: null });
                  load();
                }}>Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <MiniTable columns={[
            { label: "Nom", render: (d) => <span className="font-medium">{d.name}</span> },
            { label: "Type", render: (d) => d.doc_type },
            { label: "Ajouté le", render: (d) => fmtDate(d.created_at) },
            { label: "Par", render: (d) => d.uploaded_by },
            { label: "", render: (d) => (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" data-testid={`view-doc-${d.id}`} onClick={async () => {
                  const r = await api.get(`/documents/${d.id}/file`);
                  if (r.data?.data) window.open(r.data.data, "_blank");
                }}><Download className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-500" data-testid={`delete-doc-${d.id}`} onClick={async () => { await api.delete(`/documents/${d.id}`); load(); }}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ) },
          ]} rows={documents} empty="Aucun document joint" />
        </TabsContent>

        <TabsContent value="historique" className="mt-4">
          <h3 className="font-heading mb-4 font-semibold text-slate-800">Chronologie complète du véhicule</h3>
          <div className="relative ml-3 space-y-4 border-l-2 border-slate-200 pl-6" data-testid="vehicle-timeline">
            {timeline.length === 0 && <p className="text-sm text-slate-400">Aucun événement.</p>}
            {timeline.map((t, i) => (
              <div key={i} className="relative">
                <span className={`absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-white ${{ status: "bg-blue-500", expense: "bg-amber-500", maintenance: "bg-rose-500", rental: "bg-purple-500", sale: "bg-emerald-500" }[t.kind] || "bg-slate-400"}`} />
                <p className="text-xs text-slate-400">{fmtDate(t.date)}</p>
                <p className="text-sm font-semibold text-slate-800">{t.label}</p>
                {t.detail && <p className="text-xs text-slate-500">{t.detail}</p>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="rentabilite" className="mt-4 space-y-4">
          <h3 className="font-heading font-semibold text-slate-800">Analyse de rentabilité réelle</h3>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b"><td className="py-2.5 text-slate-600">Prix d'achat ({v.purchase_currency})</td><td className="py-2.5 text-right font-mono-num font-semibold">{fmt(v.purchase_price_fcfa)}</td></tr>
                {Object.entries(expenses.reduce((acc, e) => { acc[e.group] = (acc[e.group] || 0) + e.amount_fcfa; return acc; }, {})).map(([g, total]) => (
                  <tr key={g} className="border-b"><td className="py-2.5 pl-4 text-slate-500">+ {g}</td><td className="py-2.5 text-right font-mono-num text-amber-700">{fmt(total)}</td></tr>
                ))}
                <tr className="border-b bg-blue-50/60"><td className="py-2.5 font-bold text-slate-900">COÛT DE REVIENT TOTAL</td><td className="py-2.5 text-right font-mono-num font-bold text-blue-700" data-testid="profitability-cost-basis">{fmt(v.cost_basis)}</td></tr>
                {v.budget_total > 0 && (
                  <tr className="border-b"><td className="py-2.5 text-slate-600">Budget prévu / écart</td>
                    <td className={`py-2.5 text-right font-mono-num font-semibold ${v.cost_basis > v.budget_total ? "text-red-600" : "text-emerald-600"}`} data-testid="budget-line">
                      {fmt(v.budget_total)} / {v.cost_basis > v.budget_total ? `dépassé de ${fmt(v.cost_basis - v.budget_total)}` : fmt(v.budget_total - v.cost_basis) + " restants"}
                    </td>
                  </tr>
                )}
                <tr className="border-b"><td className="py-2.5 text-slate-600">Prix minimum conseillé (marge minimale)</td><td className="py-2.5 text-right font-mono-num font-semibold text-amber-700" data-testid="min-sale-price">{fmt(v.min_sale_price)}</td></tr>
                {v.profit == null && v.forecast_profit != null && (
                  <tr className="border-b"><td className="py-2.5 text-slate-600">Bénéfice prévisionnel (prix cible)</td><td className={`py-2.5 text-right font-mono-num font-semibold ${v.forecast_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(v.forecast_profit)} ({v.forecast_margin} %)</td></tr>
                )}
                <tr className="border-b"><td className="py-2.5 text-slate-600">Revenus de location</td><td className="py-2.5 text-right font-mono-num text-purple-600">{fmt(v.rental_revenue)}</td></tr>
                <tr className="border-b"><td className="py-2.5 text-slate-600">Prix de vente</td><td className="py-2.5 text-right font-mono-num font-semibold">{v.sale_price != null ? fmt(v.sale_price) : "—"}</td></tr>
                <tr className="bg-emerald-50"><td className="py-3 font-bold text-slate-900">BÉNÉFICE NET</td>
                  <td className={`py-3 text-right font-mono-num text-lg font-bold ${(v.profit ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="profitability-net-profit">
                    {v.profit != null ? `${fmt(v.profit)} (${v.margin_pct}%)` : "Non vendu"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
